import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FDPrimaryActionButton,
  FDTextButton,
  FDStack,
  FDTitleText,
  FDBodyText,
  FDHeadlineText,
  FDPaper,
  FDAccordionGroup,
  FDAccordionDetails, FDSecondaryActionButton, FDSimpleAccordion, FDBox,
} from '@facet-wealth/design';
import { dataAttrs, DataValueMap } from 'Helpers/Testing';
import DataContainer from 'Components/UI/DataContainer';
import Relationship from 'facet-rest/src/domain/Relationship';
import useLink from 'Hooks/useLink';
import { MetaCollectionRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaCollectionRenderer';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import TaxStrategyGainsBudgetCalculatorMetaOverride
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/TaxStrategy/TaxStrategyGainsBudgetCalculatorMetaOverrideSet';
import { useSnackbarContext } from 'Context/SnackbarContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import TaxStrategyMetaOverride
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/TaxStrategy/TaxStrategyMetaOverrideSet';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { FileViewer } from 'Components/ComponentLibrary/Components/Uncategorized/FileViewer';

const TaxStrategySettingsCurrentAndNextYearLayout = () => {
  const { resource } = useMetaLayoutContext();
  return (
    <FDStack direction={'row'} spacing={2}>
      <MetaControlField attr={'currentYearValue'} {...dataAttrs('gains-budget-calculator-current-year-value', resource)} />
      <MetaControlField attr={'nextYearValue'} {...dataAttrs('gains-budget-calculator-next-year-value', resource)} />
    </FDStack>
  );
};

export const TaxStrategyGainsBudgetCalculatorLayout = ({ taxSettingsResource, onClose, setGainsBudgetCalculatorCollection }) => {
  const { resource } = useMetaLayoutContext();
  const { displayErrorMessage } = useSnackbarContext();
  const [submitting, setSubmitting] = React.useState(false);
  const [statementsPreviewOpen, setStatementsPreviewOpen] = React.useState(false);

  const wasCalculated = resource.isTruthy('wasCalculated');

  const onCalculateButtonClick = useCallback(async () => {
    try {
      setSubmitting(true);
      const response = await resource.submit();
      setGainsBudgetCalculatorCollection(response);
    } catch (err) {
      if (err.errors) {
        displayErrorMessage(err, err.errors);
      } else {
        displayErrorMessage(err, 'Error: failed to save account allocations');
      }
    } finally {
      setSubmitting(false);
    }
  }, [setGainsBudgetCalculatorCollection, resource, displayErrorMessage]);

  const onOpenStatementsViewClick = useCallback(async () => {
    setStatementsPreviewOpen(true);
  }, []);

  const applyChangesButton = (
    <FDPrimaryActionButton
      {...dataAttrs('gains-budget-save-and-close-button')}
      label={'Apply budgets'}
      icon={'chevronRight'}
      iconPosition={'end'}
      onClick={async () => {
        try {
          setSubmitting(true);
          await resource.submit();
          onClose();
        } catch (err) {
          if (err.errors) {
            displayErrorMessage(err, err.errors);
          } else {
            displayErrorMessage(err, 'Error: failed to gains budget');
          }
        } finally {
          setSubmitting(false);
        }
      }}
      loading={submitting}
    />
  );

  const firstCalculateButton = (
    <FDPrimaryActionButton
      {...dataAttrs('gains-budget-first-calculate-min-max-budget-button')}
      label={'Calculate min and max budget'}
      onClick={onCalculateButtonClick}
      loading={submitting}
    />
  );

  const recalculateButton = (
    <FDSecondaryActionButton
      {...dataAttrs('gains-budget-recalculate-min-max-budget-button')}
      label={'Re-Calculate'}
      onClick={onCalculateButtonClick}
      loading={submitting}
      icon={'refresh'}
    />
  );

  return (
    <FDStack direction={'row'} spacing={4}>
      <FDStack direction={'column'} spacing={4}>
        <FDTitleText size={'md'} value={'1. Use the member statement to enter the missing data.'} />
        <FDStack direction={'column'} spacing={2}>
          {resource.attr('statements')?.resources().length() > 0 && (
          <FDBox>
            <FDSecondaryActionButton
              {...dataAttrs('gains-budget-open-statements-button')}
              label={'View brokerage statement(s)'}
              icon={'visibility'}
              onClick={onOpenStatementsViewClick}
            />
          </FDBox>
          )}
          <FDStack direction={'column'} spacing={1}>
            <FDTitleText size={'sm'} value={'Portfolio Information'} />
            <div {...dataAttrs('portfolioMarketValue')}>
              <MetaControlField attr={'portfolioMarketValue'} />
            </div>
            <div {...dataAttrs('targetBrokerageAccountStockPercent')}>
              <MetaControlField attr={'targetBrokerageAccountStockPercent'} />
            </div>
            <div {...dataAttrs('largestSingleStockMarketValue')}>
              <MetaControlField attr={'largestSingleStockMarketValue'} />
            </div>
            <div {...dataAttrs('largestConcentratedFundMarketValue')}>
              <MetaControlField attr={'largestConcentratedFundMarketValue'} />
            </div>
            <div {...dataAttrs('bondFundsMarketValue')}>
              <MetaControlField attr={'bondFundsMarketValue'} />
            </div>
            <div {...dataAttrs('cashMarketValue')}>
              <MetaControlField attr={'cashMarketValue'} />
            </div>
            <div {...dataAttrs('holdingsWithLossMarketValue')}>
              <MetaControlField attr={'holdingsWithLossMarketValue'} />
            </div>
            <div {...dataAttrs('totalPortfolioDollarGain')}>
              <MetaControlField attr={'totalPortfolioDollarGain'} />
            </div>
          </FDStack>

          <FDStack direction={'column'} spacing={1}>
            <FDTitleText size={'sm'} value={'Taxable Income & Other Planned Gains'} />
            <div {...dataAttrs('currentYearTaxableIncomeAndOtherPlannedGains')}>
              <MetaControlField attr={'currentYearTaxableIncomeAndOtherPlannedGains'} />
            </div>
            <div {...dataAttrs('nextYearTaxableIncomeAndOtherPlannedGains')}>
              <MetaControlField attr={'nextYearTaxableIncomeAndOtherPlannedGains'} />
            </div>
            <div {...dataAttrs('filingStatus')}>
              <MetaControlField attr={'filingStatus'} />
            </div>
          </FDStack>
        </FDStack>
      </FDStack>


      <FDStack direction={'column'} spacing={4}>
        <FDTitleText size={'md'} value={'2. Click "Calculate" when all fields are complete.'} />
        <FDPaper padding={4} appearance="elevated" flex={1}>
          {!wasCalculated ? (
            <FDStack direction={'column'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
              {firstCalculateButton}
            </FDStack>
          ) : (
            <FDStack direction={'column'} alignItems={'flex-start'} justifyContent={'center'} spacing={4}>
              <FDHeadlineText value={'Budget Output'} size={'sm'} />
              <FDStack direction={'column'} spacing={1}>
                <FDTitleText size={'sm'} value={'Risk Based Cap Gains Budget (Min)'} />
                <FDHeadlineText value={resource.attr('riskBasedCapGainsBudgetMin')} size={'sm'} />
              </FDStack>
              <FDStack direction={'column'} spacing={1}>
                <FDTitleText size={'sm'} value={'Tax Based Cap Gains Budget (Max)'} />
                <FDHeadlineText
                  value={resource.isTruthy('taxBasedCapGainsBudgetMaxHasNoLimit')
                    ? resource.metaField('taxBasedCapGainsBudgetMaxHasNoLimit').attr('prompt') : resource.attr('taxBasedCapGainsBudgetMax')}
                  size={'sm'}
                />
              </FDStack>
              <FDStack direction={'column'} spacing={1}>
                <FDBodyText size={'md'} value={'Set a budget for the current and next year within the calculated range'} />
                <MetaResourceRenderer
                  resource={taxSettingsResource}
                  metaOverrideSet={TaxStrategyMetaOverride}
                  layout={TaxStrategySettingsCurrentAndNextYearLayout}
                />
              </FDStack>
              <FDStack direction={'row'} spacing={1}>
                {wasCalculated ? recalculateButton : firstCalculateButton}
                {applyChangesButton}
              </FDStack>
            </FDStack>
          )}
        </FDPaper>
      </FDStack>

      <FDStack direction={'column'} spacing={4}>
        <FDTitleText size={'md'} value={'3. Review the tests to dig into the output.'} />
        <FDPaper
          appearance="flat"
          color="subtle"
          flex={1}
        >
          {!wasCalculated ? (
            <FDStack direction={'column'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
              <FDTitleText value={'Run calculation to view tests'} size={'md'} color={'textDisabled'} />
            </FDStack>
          ) : (
            <FDAccordionGroup>
              <FDSimpleAccordion title={'Step 1 - 20% Stock Diversification Test'} expansionID="step1" data-test="gains-budget-calc-step-1">
                <FDAccordionDetails>
                  <FDStack direction={'column'} spacing={1}>
                    <MetaControlField attr={'singleStockLimit'} />
                    <MetaControlField attr={'largestStock'} />
                    <MetaControlField attr={'marketValueDifferenceSdt'} />
                    <MetaControlField attr={'percentOfPortfolioSdt'} />
                    <MetaControlField attr={'addToCapGainsBudgetSdt'} />
                  </FDStack>
                </FDAccordionDetails>
              </FDSimpleAccordion>
              <FDSimpleAccordion title={'Step 2 - Concentrated ETF Test'} expansionID="step2" data-test="gains-budget-calc-step-2">
                <FDAccordionDetails>
                  <FDStack direction={'column'} spacing={1}>
                    <MetaControlField attr={'concentratedFundLimit'} />
                    <MetaControlField attr={'largestConcentratedFund'} />
                    <MetaControlField attr={'marketValueDifference'} />
                    <MetaControlField attr={'percentOfPortfolioGainsEtfTest'} />
                    <MetaControlField attr={'addToCapGainsBudgetEtfTest'} />
                  </FDStack>
                </FDAccordionDetails>
              </FDSimpleAccordion>
              <FDSimpleAccordion title={'Step 3 - Within 10% Bond Allocation Test'} expansionID="step3" data-test="gains-budget-calc-step-3">
                <FDAccordionDetails>
                  <FDStack direction={'column'} spacing={1}>
                    <MetaControlField attr={'targetBondMarketValue'} />
                    <MetaControlField attr={'currentBondsBat'} />
                    <MetaControlField attr={'cashAndCashRaisedSoFar'} />
                    <MetaControlField attr={'marketValueDifferenceBat'} />
                    <MetaControlField attr={'percentDifferenceBat'} />
                    <MetaControlField attr={'addToCapGainsBudgetBat'} />
                  </FDStack>
                </FDAccordionDetails>
              </FDSimpleAccordion>
              <FDSimpleAccordion title={'Step 4 - 20% DI Cash Test'} expansionID="step4" data-test="gains-budget-calc-step-4">
                <FDAccordionDetails>
                  <FDStack direction={'column'} spacing={1}>
                    <MetaControlField attr={'targetCashMarketValueDct'} />
                    <MetaControlField attr={'cashAvailableDct'} />
                    <MetaControlField attr={'marketValueDifferenceDct'} />
                    <MetaControlField attr={'percentDifferenceDct'} />
                    <MetaControlField attr={'addToCapGainsBudgetDct'} />
                  </FDStack>
                </FDAccordionDetails>
              </FDSimpleAccordion>
              <FDSimpleAccordion title={'Step 5 - Tax Bracket Test'} expansionID="step5" data-test="gains-budget-calc-step-5">
                <FDAccordionDetails>
                  <FDStack direction={'column'} spacing={1}>
                    <MetaControlField attr={'currentBracket'} />
                    <MetaControlField attr={'spaceToTwentyPercentCurrentYear'} />
                    <MetaControlField attr={'nextYearBracket'} />
                    <MetaControlField attr={'spaceToTwentyPercentNextYear'} />
                    {resource.isTruthy('taxInformedMaxGainsBudgetHasNoLimit') ? (
                      <FDBodyText size={'sm'} value={resource.metaField('taxInformedMaxGainsBudgetHasNoLimit').attr('prompt')} />
                    ) : <MetaControlField attr={'taxInformedMaxGainsBudget'} />}
                  </FDStack>
                </FDAccordionDetails>
              </FDSimpleAccordion>
            </FDAccordionGroup>
          )}
        </FDPaper>
      </FDStack>
      {resource.attr('statements')?.resources().length() > 0 && (
        <FileViewer
          attachmentResources={resource.attr('statements').resources().list()}
          isOpen={statementsPreviewOpen}
          closeModal={() => {
            setStatementsPreviewOpen(false);
          }}
          eventCategory={'Gains Budget Calculator Statements Viewer'}
        />
      )}
    </FDStack>
  );
};

TaxStrategyGainsBudgetCalculatorLayout.propTypes = {
  taxSettingsResource: PropTypes.object.isRequired,
};

const TaxStrategyGainsBudgetCalculatorView = ({
  open,
  onClose,
  taxStrategyGainsBudgetCalculatorRelationship,
  taxSettingsResource,
}) => {
  const {
    isInitializing,
    loadingError,
    collection,
    setCollection,
  } = useLink(taxStrategyGainsBudgetCalculatorRelationship.link('related'));

  return (
    <FDStack
      direction={'column'}
      spacing={3}
      width={'100%'}
      open={open}
      onClose={onClose}
      dialogWidth={'lg'}
      {...DataValueMap.init('tax-strategy-gains-calculator-budget-view').build()}
    >
      <FDStack direction={'row'} spacing={2} alignItems={'center'}>
        <FDHeadlineText value={'Capital Gains Budget Calculator'} size={'sm'} color={'textSecondary'} />
        <FDTextButton
          {...dataAttrs('gains-budget-close-button')}
          label={'Close'}
          onClick={() => onClose()}
        />
      </FDStack>
      <DataContainer
        loading={isInitializing}
        loadingError={loadingError}
        component={() => (
          <MetaCollectionRenderer
            collection={collection}
            layout={TaxStrategyGainsBudgetCalculatorLayout}
            metaOverrideSet={TaxStrategyGainsBudgetCalculatorMetaOverride}
            LayoutProps={{
              taxSettingsResource,
              onClose,
              setGainsBudgetCalculatorCollection: setCollection,
            }}
          />
        )}
      />
    </FDStack>
  );
};

TaxStrategyGainsBudgetCalculatorView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  taxStrategyGainsBudgetCalculatorRelationship: PropTypes.instanceOf(Relationship).isRequired,
  taxSettingsResource: PropTypes.object.isRequired,
};

export { TaxStrategyGainsBudgetCalculatorView };
